import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {WIND_GUST_KIND} from '../../../configs/biomeEffects/windGust';
import {resolveSpawnBox} from './biomeContext';
import {GUST_LEAF, createGustLeaves, resolveGustLeafSway, writeGustLeafPose} from './gustLeaves';
import {
  TWO_PI,
  hash01,
  lerp,
  pickPointInBox,
  randomIn,
  randomInt,
  randomRange,
  resolveLifeEnvelope,
  resolveOvalFade,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ПОРЫВ ВЕТРА НАД ЛУГОМ (зеленые подложки). Чистые функции без Phaser и DOM:
 * по этому описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Порыв проходит через поле по ветру матча (тому же, что несет облака). В нем:
 *  - травяные волны: несколько гребней идут по ветру один за другим. Гребень —
 *    светлая полоса ПОПЕРЕК ветра (трава легла и повернулась к свету), сразу
 *    за ней — полоса темнее (трава в тени);
 *  - листья над травой: летят вместе с порывом, качаются поперек ветра,
 *    кружатся и трепещут (сам лист — ./gustLeaves: те же листья летят с
 *    вуалями ветра над другой половиной лугов, ./windVeils).
 *
 * ГРЕБЕНЬ — не прямая полоса, а дуга: середина уходит вперед по ветру, поверх
 * дуги — медленная волна. Он собран из коротких прядей травы, уложенных
 * внахлест вдоль этой дуги, каждая — по касательной к ней: полоса выходит
 * сплошной, гнется и меняет яркость по длине. Перекос и медленная волна у
 * гребней одного порыва общие и лежат на земле: гребни изгибаются согласно,
 * идут почти параллельно и друг друга не пересекают.
 *
 * ПЕРЕЛИВЫ. Яркость вдоль гребня задают светлые пятна, лежащие на самой земле:
 * гребень, проходя, зажигает то одно место, то другое, а сами пятна медленно
 * дышат. Яркость — гладкая функция места и времени: трава переливается, а не
 * мигает.
 *
 * Гребень живет несколько секунд: проступает, катится с порывом и тает, потом
 * проступает снова чуть в стороне. Где он на каждой своей жизни — функция
 * номера жизни, поэтому поза остается функцией времени.
 *
 * Волны катятся только по открытому лугу подложки и только по полю: на лесах,
 * скалах и воде вокруг их нет.
 *
 * Дорожка порыва помечена (WIND_GUST_TAG): по метке сцена находит идущий порыв
 * и, когда он в кадре игрока, играет шелест ветра в траве (см.
 * scenes/FieldEffectSounds). Рождаться порыву метка не мешает: сцена его не
 * придерживает. Метка — про то, ЧТО это за дорожка, а не про звук: тем же
 * построителем собран морской бриз острова (./sunStars), и метка у него та же;
 * какой подложке какой звук, решает перечень привязок
 * (configs/fieldEffectSounds) — у бриза тот же шелест, что у порыва над лугом.
 */

/** Метка дорожки порыва ветра: по ней сцена находит идущий порыв */
export const WIND_GUST_TAG = 'windGust';

const WAVE_LIGHT = 'waveLight';
const WAVE_SHADE = 'waveShade';
const LEAF = GUST_LEAF;

const createTracks = (context, params) => {
  const {hexSize, wind, fieldBox, coverage} = context;
  const {waves, leaves} = params;
  const across = {x: -wind.y, y: wind.x};
  const plain = params.plain ? context.toWorldOval(params.plain) : null;
  const patchAcross = hexSize * params.patchAcrossFactor;
  const patchAlong = hexSize * params.patchAlongFactor;
  const fieldCorners = [
    [fieldBox.left, fieldBox.top], [fieldBox.right, fieldBox.top],
    [fieldBox.left, fieldBox.bottom], [fieldBox.right, fieldBox.bottom],
  ];

  /**
   * Общее на все гребни порыва: перекос, медленная волна вдоль гребней и пятна
   * переливов. Все это лежит на земле и с порывом не едет
   */
  const createFlow = (rng) => ({
    // Перекос: на сколько гребень уходит по ветру на единицу расстояния поперек него
    slant: Math.tan(toRadians(randomRange(rng, -waves.slantDeg, waves.slantDeg))),
    wiggleAmplitude: hexSize * randomIn(rng, waves.wiggle.amplitudeFactor),
    wiggleLength: hexSize * randomIn(rng, waves.wiggle.lengthFactor),
    wiggleRun: hexSize * randomIn(rng, waves.wiggle.runFactor),
    wigglePhase: rng() * TWO_PI,
    // Два слоя пятен переливов. Слои скошены в разные стороны: вместе дают
    // пятна, а не полосы. Первый лежит неподвижно, второй еще и дышит:
    // pacePerMs — сколько раз за миллисекунду он успевает разгореться и погаснуть
    patches: [0, 1].map((index) => ({
      length: hexSize * randomIn(rng, waves.shimmer.patchFactor),
      run: hexSize * randomIn(rng, waves.shimmer.runFactor) * (index === 0 ? 1 : -1),
      phase: rng() * TWO_PI,
      pacePerMs: index === 0 ? 0 : 1 / randomIn(rng, waves.shimmer.periodMs),
    })),
  });

  /** Гребни порыва: пряди светлой полосы и тени за ними */
  const createWaveParticles = (rng) => {
    const particles = [];
    const count = randomInt(rng, waves.count.min, waves.count.max);
    // Места гребней вдоль ветра: один за другим, от головы порыва к хвосту
    const slots = [0];
    for (let index = 1; index < count; index += 1) {
      slots.push(slots[index - 1] - hexSize * randomIn(rng, waves.gapFactor));
    }
    const trainLength = -slots[count - 1];

    for (let index = 0; index < count; index += 1) {
      const length = hexSize * randomIn(rng, waves.lengthFactor);
      const pieceCount = Math.max(2, Math.round(length / (hexSize * waves.pieceStepFactor)));
      const step = length / pieceCount;
      const thickness = hexSize * randomIn(rng, waves.thicknessFactor);
      // Общее для всех прядей гребня
      const front = {
        frontIndex: index,
        // Место гребня в порыве: вдоль ветра (середина строя — у середины порыва) и поперек него
        slot: slots[index] + trainLength / 2,
        lane: hexSize * randomRange(rng, -waves.laneFactor, waves.laneFactor),
        // Зерно гребня: по нему и номеру жизни выбирается сдвиг вбок
        seed: 1 + index + rng(),
        length,
        // Дуга: на столько середина гребня впереди его концов
        sag: length * randomIn(rng, waves.bowShare),
        alpha: randomIn(rng, waves.alpha),
        lifeMs: randomIn(rng, waves.lifeMs),
        phaseMs: rng() * waves.lifeMs.max,
      };
      for (let piece = 0; piece < pieceCount; piece += 1) {
        // Место пряди на гребне: от -1 (один конец) до 1 (другой)
        const share = ((piece + 0.5) / pieceCount) * 2 - 1;
        const taper = 1 - share * share;
        const body = {
          front,
          share,
          place: (share * length) / 2,
          // Край гребня неровный: прядь чуть сдвинута вдоль ветра и чуть толще или тоньше соседних
          nudge: hexSize * randomRange(rng, -waves.pieceNudgeFactor, waves.pieceNudgeFactor),
          // К концам гребень сужается и бледнеет
          thickness: thickness * lerp(waves.endThicknessShare, 1, taper)
            * (1 + randomRange(rng, -waves.pieceThicknessJitter, waves.pieceThicknessJitter)),
          ends: taper ** waves.endFadePower,
        };
        // Тень — под светлой полосой (ее слой ниже) и своим мягким пятном
        particles.push({
          ...body,
          kind: WAVE_SHADE,
          layer: FIELD_BIOME_LAYER_KEYS.GROUND,
          texture: FIELD_BIOME_TEXTURES.DOT,
          tint: params.waveTints.shade,
          length: step * waves.shade.overlap,
          thickness: body.thickness * waves.shade.thicknessShare,
        });
        particles.push({
          ...body,
          kind: WAVE_LIGHT,
          layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
          texture: FIELD_BIOME_TEXTURES.SWATH,
          tint: params.waveTints.light,
          length: step * waves.pieceOverlap,
        });
      }
    }
    return particles;
  };

  const buildGroup = ({rng, view}) => {
    // Точка поля, через которую пройдет середина порыва: там, куда смотрит камера
    const through = pickPointInBox(rng, resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0}));
    // Путь вдоль ветра: от подхода к полю до ухода за него
    const alongs = fieldCorners.map(([x, y]) => (x - through.x) * wind.x + (y - through.y) * wind.y);
    const runway = hexSize * params.runwayFactor + patchAlong / 2;
    const from = Math.min(...alongs) - runway;
    const to = Math.max(...alongs) + runway;
    const speed = hexSize * randomIn(rng, params.speedFactor);
    const flow = createFlow(rng);
    const particles = [...createWaveParticles(rng), ...createGustLeaves(rng, leaves, hexSize)];

    return {durationMs: ((to - from) / speed) * 1000, through, from, speed, flow, particles};
  };

  /** Яркость травы в этом месте в этот момент: единица — пятно горит, меньше — трава тусклее */
  const resolveShimmer = ({patches}, acrossPlace, along, elapsedMs) => {
    let glow = 0.5;
    for (let index = 0; index < patches.length; index += 1) {
      const patch = patches[index];
      glow += 0.25 * Math.sin(
        TWO_PI * (acrossPlace / patch.length + along / patch.run + elapsedMs * patch.pacePerMs) + patch.phase
      );
    }
    return 1 - waves.shimmer.depth * (1 - smoothstep(waves.shimmer.rise.from, waves.shimmer.rise.to, glow));
  };

  const resolveWavePose = (group, particle, elapsedMs, gustEnvelope, out) => {
    const {front} = particle;
    const {flow} = group;
    // Жизнь гребня: на каждой — свой сдвиг вбок
    const since = (elapsedMs + front.phaseMs) / front.lifeMs;
    const cycle = Math.floor(since);
    const lifeEnvelope = resolveLifeEnvelope(since - cycle, waves.fadeShare, waves.fadeShare);
    if (!(lifeEnvelope > 0)) {
      return writeHiddenPose(out);
    }
    // Гребень идет с порывом: его середина вдоль ветра и место пряди поперек него
    const frontAlong = group.from + (group.speed * elapsedMs) / 1000 + front.slot;
    const acrossPlace = front.lane + (hash01(front.seed, cycle) * 2 - 1) * hexSize * waves.laneJitterFactor
      + particle.place;
    // Дуга (концы отстают от середины), общий перекос и общая медленная волна
    const wiggleAngle = TWO_PI * (acrossPlace / flow.wiggleLength + frontAlong / flow.wiggleRun) + flow.wigglePhase;
    const crestAlong = frontAlong - front.sag * particle.share * particle.share + flow.slant * acrossPlace
      + flow.wiggleAmplitude * Math.sin(wiggleAngle) + particle.nudge;
    // Наклон гребня к перпендикуляру ветра в этом месте: прядь лежит по касательной
    const slope = (-4 * front.sag * particle.share) / front.length + flow.slant
      + flow.wiggleAmplitude * (TWO_PI / flow.wiggleLength) * Math.cos(wiggleAngle);
    // Тень — сразу за светлой полосой, с наветренной стороны
    const along = particle.kind === WAVE_SHADE ? crestAlong - hexSize * waves.shade.offsetFactor : crestAlong;
    const x = group.through.x + wind.x * along + across.x * acrossPlace;
    const y = group.through.y + wind.y * along + across.y * acrossPlace;
    // Волны — только по траве поля
    const ground = resolveOvalFade(plain, x, y) * smoothstep(0.3, 0.55, coverage.sample(x, y));
    // Переливы у тени те же, что у ее светлой полосы: гаснут и разгораются вместе
    const alpha = front.alpha * particle.ends * resolveShimmer(flow, acrossPlace, crestAlong, elapsedMs)
      * lifeEnvelope * gustEnvelope * ground * (particle.kind === WAVE_SHADE ? waves.shade.alphaShare : 1);
    // Кадр пряди смотрит передней кромкой по ветру: ось X кадра — вдоль гребня. Где гребень идет к ветру
    // наискось, пряди стоят вдоль него реже — кадр там во столько же раз длиннее, и нахлест остается прежним
    return writePose(
      out, x, y, alpha, particle.length * Math.hypot(1, slope), particle.thickness,
      Math.atan2(across.y + wind.y * slope, across.x + wind.x * slope)
    );
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const fadeShare = Math.min(0.45, params.fadeMs / group.durationMs);
    const gustEnvelope = resolveLifeEnvelope(elapsedMs / group.durationMs, fadeShare, fadeShare);
    if (!(gustEnvelope > 0)) {
      return writeHiddenPose(out);
    }
    if (particle.kind !== LEAF) {
      return resolveWavePose(group, particle, elapsedMs, gustEnvelope, out);
    }

    // Лист летит с порывом, чуть обгоняя его или отставая
    const along = group.from + (group.speed * particle.speedShare * elapsedMs) / 1000 + particle.alongShare * patchAlong;
    const acrossOffset = particle.acrossShare * patchAcross + resolveGustLeafSway(particle, elapsedMs);
    return writeGustLeafPose(
      out,
      leaves,
      particle,
      group.through.x + wind.x * along + across.x * acrossOffset,
      group.through.y + wind.y * along + across.y * acrossOffset,
      leaves.alpha * gustEnvelope,
      elapsedMs
    );
  };

  return [{
    tag: WIND_GUST_TAG,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: 1},
    buildGroup,
    resolvePose,
  }];
};

const windGust = {
  kind: WIND_GUST_KIND,
  textures: [FIELD_BIOME_TEXTURES.SWATH, FIELD_BIOME_TEXTURES.DOT, FIELD_BIOME_TEXTURES.LEAF],
  createTracks,
};

export default windGust;
